/**
 * Technique Card Component
 * Displays a single Lumo technique with steps and examples
 */

import React from 'react';
import { cn } from '../utils/cn';
import Icon from './AppIcon';

const CATEGORY_CONFIG = {
  acknowledge_pivot: {
    icon: 'ArrowLeftRight',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    label: 'Acknowledge & Pivot'
  },
  detached_close: {
    icon: 'Hand',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    label: 'Detached Close'
  },
  speed_drill: {
    icon: 'Zap',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    label: 'Speed Drill'
  },
  cascade_challenge: {
    icon: 'Flame',
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    label: 'Cascade Challenge'
  },
  reset_move: {
    icon: 'RotateCcw',
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    label: 'Reset Move'
  },
  frame_flip: {
    icon: 'Lightbulb',
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    label: 'Frame Flip'
  },
  exit_ramp: {
    icon: 'ArrowRightFromLine',
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    label: 'Exit Ramp'
  },
  concept: {
    icon: 'BookOpen',
    color: 'text-indigo-700',
    bgColor: 'bg-indigo-50',
    label: 'Concept'
  }
};

const DIFFICULTY_CONFIG = {
  beginner: {
    color: 'text-green-700',
    bgColor: 'bg-green-100',
    label: 'Beginner'
  },
  intermediate: {
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-100',
    label: 'Intermediate'
  },
  advanced: {
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    label: 'Advanced'
  }
};

const TechniqueCard = ({ 
  technique, 
  userMastery = null, 
  onPractice = null,
  showFullDetails = false,
  showIndustryContext = false,
  className = ''
}) => {
  const categoryConfig = CATEGORY_CONFIG[technique.category] || CATEGORY_CONFIG.acknowledge_pivot;
  const difficultyConfig = DIFFICULTY_CONFIG[technique.difficultyLevel] || DIFFICULTY_CONFIG.intermediate;

  return (
    <div className={cn(
      'bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-primary-300 transition-all relative',
      className
    )}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4 relative z-10">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <div className={cn('p-2 rounded-lg', categoryConfig.bgColor)}>
              <Icon name={categoryConfig.icon} className={cn('w-5 h-5', categoryConfig.color)} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">{technique.name}</h3>
              <div className="flex items-center gap-2">
                <p className="text-sm text-gray-500">{categoryConfig.label}</p>
                {showIndustryContext && technique.industry && technique.industry !== 'universal' && (
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-semibold uppercase">
                    {technique.industry}
                  </span>
                )}
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-600 italic">{technique.tagline}</p>
        </div>

        {/* Difficulty badge */}
        <div className={cn(
          'px-3 py-1 rounded-full text-xs font-semibold',
          difficultyConfig.bgColor,
          difficultyConfig.color
        )}>
          {difficultyConfig.label}
        </div>
      </div>

      {/* Success rate */}
      {technique.avgSuccessRate && (
        <div className="mb-4 flex items-center gap-2">
          <div className="flex-1 bg-gray-200 rounded-full h-2">
            <div 
              className="bg-primary-500 h-2 rounded-full transition-all"
              style={{ width: `${technique.avgSuccessRate * 100}%` }}
            />
          </div>
          <span className="text-sm font-semibold text-gray-700">
            {Math.round(technique.avgSuccessRate * 100)}% success
          </span>
        </div>
      )}

      {/* User mastery */}
      {userMastery && (
        <div className="mb-4 p-3 bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg border border-primary-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">Your Mastery</p>
              <p className="text-lg font-bold text-primary-700 capitalize">{userMastery.masteryLevel}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-600">Attempts</p>
              <p className="text-lg font-bold text-gray-900">{userMastery.timesAttempted}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-600">Success</p>
              <p className="text-lg font-bold text-green-600">
                {userMastery.successRate ? Math.round(userMastery.successRate * 100) : 0}%
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Steps */}
      {showFullDetails && (
        <div className="space-y-3 mb-4">
          <h4 className="text-sm font-semibold text-gray-700">How It Works:</h4>
          {technique.step1 && (
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-bold">
                1
              </div>
              <p className="text-sm text-gray-700">{technique.step1}</p>
            </div>
          )}
          {technique.step2 && (
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-bold">
                2
              </div>
              <p className="text-sm text-gray-700">{technique.step2}</p>
            </div>
          )}
          {technique.step3 && (
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-bold">
                3
              </div>
              <p className="text-sm text-gray-700">{technique.step3}</p>
            </div>
          )}
        </div>
      )}

      {/* Example */}
      {showFullDetails && technique.exampleExecution && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Example</h4>
          <p className="text-sm text-gray-900 font-medium">{technique.exampleExecution}</p>
        </div>
      )}

      {/* Timing note */}
      {showFullDetails && technique.timingNote && (
        <div className="mb-4 flex items-start gap-2">
          <Icon name="Info" className="w-5 h-5 text-blue-500 flex-shrink-0" />
          <p className="text-xs text-gray-600">{technique.timingNote}</p>
        </div>
      )}

      {/* Best for tags */}
      {showFullDetails && Array.isArray(technique.bestForObjections) && technique.bestForObjections.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Best For</h4>
          <div className="flex flex-wrap gap-2">
            {technique.bestForObjections.slice(0, 5).map((objection, index) => (
              <span 
                key={index}
                className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
              >
                {objection}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Industry-specific context */}
      {showIndustryContext && technique.industryNote && (
        <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start gap-2">
            <Icon name="Lightbulb" className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-xs font-semibold text-blue-900 uppercase mb-1">Industry Context</h4>
              <p className="text-xs text-blue-800">{technique.industryNote}</p>
            </div>
          </div>
        </div>
      )}

      {/* Industry metrics */}
      {showIndustryContext && technique.industryMetrics && technique.industryMetrics.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Industry Metrics</h4>
          <div className="flex flex-wrap gap-2">
            {technique.industryMetrics.map((metric, index) => (
              <span 
                key={index}
                className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-semibold"
              >
                {metric}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Practice button */}
      {onPractice && (
        <button
          onClick={() => onPractice(technique)}
          className="w-full mt-4 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 relative z-10 cursor-pointer"
        >
          <Icon name="Play" className="w-5 h-5" />
          Practice This Technique
        </button>
      )}
    </div>
  );
};

export default TechniqueCard;
